import React, { useRef, useState, useCallback, useEffect } from 'react';
import { VoiceAnnotation } from '../shared/VoiceAnnotation';
import { BrowserDetectionService } from '../../services/browserDetection.service';
import { IDetectionService, Detection } from '../../services/detection.service';
import { getAISettings } from '../../utils/aiSettings';

interface CroppedImage {
  imageData: string;
  detection: Detection;
  voiceAnnotation?: string;
}

interface CameraCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (imageData: string, voiceAnnotation?: string, croppedImages?: Array<CroppedImage>) => void;
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  isOpen,
  onClose,
  onCapture
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(true);
  const [currentVoiceAnnotation, setCurrentVoiceAnnotation] = useState('');
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
  const [pendingImageData, setPendingImageData] = useState<string | null>(null);
  const [editableDescription, setEditableDescription] = useState('');
  const [croppedImages, setCroppedImages] = useState<Array<CroppedImage>>([]);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [recordingItemIndex, setRecordingItemIndex] = useState<number | null>(null);
  const [itemVoiceNotes, setItemVoiceNotes] = useState<Map<number, string>>(new Map());
  const [currentItemTranscription, setCurrentItemTranscription] = useState<string>('');
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);
  
  // Detection state
  const [detectionService, setDetectionService] = useState<IDetectionService | null>(null);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [isInitializingDetection, setIsInitializingDetection] = useState(false);
  const [detectionError, setDetectionError] = useState<string | null>(null);
  const detectionLoopRef = useRef<number | null>(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      
      // Don't start camera if already starting or we already have a stream
      if (isCapturing || stream) {
        return;
      }
      
      // Try different camera configurations based on device capabilities
      const constraints = [
        // First try: Mobile-optimized settings
        {
          video: {
            facingMode: 'environment', // Use back camera on mobile
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        },
        // Second try: Desktop-friendly settings
        {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        },
        // Third try: Basic settings
        {
          video: true
        }
      ];
      
      let mediaStream = null;
      let lastError = null;
      
      for (const constraint of constraints) {
        try {
          // Trying camera constraint
          mediaStream = await navigator.mediaDevices.getUserMedia(constraint);
          // Camera started successfully
          break;
        } catch (err) {
          console.warn('Camera constraint failed:', constraint, err);
          lastError = err;
          continue;
        }
      }
      
      if (!mediaStream) {
        throw lastError || new Error('Unable to access camera with any configuration');
      }
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions and ensure your camera is not being used by another application.');
    }
  }, [isCapturing, stream]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  // Helper function to crop image based on bounding box
  const cropImageToBox = useCallback((
    sourceCanvas: HTMLCanvasElement,
    box: { x: number; y: number; width: number; height: number }
  ): string => {
    const cropCanvas = document.createElement('canvas');
    const cropContext = cropCanvas.getContext('2d');
    
    if (!cropContext) return '';
    
    // Add padding around the box (10% on each side)
    const padding = 0.1;
    const paddedX = Math.max(0, box.x - box.width * padding);
    const paddedY = Math.max(0, box.y - box.height * padding);
    const paddedWidth = Math.min(sourceCanvas.width - paddedX, box.width * (1 + 2 * padding));
    const paddedHeight = Math.min(sourceCanvas.height - paddedY, box.height * (1 + 2 * padding));
    
    cropCanvas.width = paddedWidth;
    cropCanvas.height = paddedHeight;
    
    // Draw the cropped portion
    cropContext.drawImage(
      sourceCanvas,
      paddedX, paddedY, paddedWidth, paddedHeight,
      0, 0, paddedWidth, paddedHeight
    );
    
    return cropCanvas.toDataURL('image/jpeg', 0.8);
  }, []);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (context) {
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64 image data
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      
      // Show processing feedback
      setIsProcessing(true);
      
      // Store the image data
      setPendingImageData(imageData);
      setEditableDescription(currentVoiceAnnotation || '');
      
      // Check AI settings for multiple items per photo
      const settings = getAISettings();
      
      // If we have detections and multi-item is enabled, crop them
      const croppedImgs: Array<{imageData: string, detection: Detection}> = [];
      if (settings.multipleItemsPerPhoto && detections.length > 0) {
        console.log(`📸 Cropping ${detections.length} detected items from photo`);
        detections.forEach((detection, index) => {
          const croppedData = cropImageToBox(canvas, detection.boundingBox);
          if (croppedData) {
            croppedImgs.push({ imageData: croppedData, detection });
            console.log(`  ✂️ Cropped ${detection.class} (${Math.round(detection.confidence * 100)}%)`);
          }
        });
      }
      
      setCroppedImages(croppedImgs);
      
      // Select all items by default
      const allIndices = new Set(croppedImgs.map((_, index) => index));
      setSelectedItems(allIndices);
      
      // Stop voice recording if active
      if (isRecordingVoice) {
        setIsRecordingVoice(false);
      }
      
      // Simulate processing time for user feedback
      setTimeout(() => {
        setIsProcessing(false);
        setShowDescriptionPopup(true);
      }, 1000);
      
      // Don't close the camera - stay open for more photos
    }
    
    setIsCapturing(false);
  }, [currentVoiceAnnotation, isRecordingVoice, detections, cropImageToBox]);

  // Voice annotation handlers
  const handleStartRecording = () => {
    setIsRecordingVoice(true);
  };

  const handleStopRecording = () => {
    setIsRecordingVoice(false);
  };

  const handlePauseRecording = () => {
    setIsRecordingVoice(false);
  };

  const handleClearRecording = () => {
    setCurrentVoiceAnnotation('');
  };

  const handleTranscriptionUpdate = (text: string) => {
    setCurrentVoiceAnnotation(text);
  };

  const handleTranscriptionComplete = (finalText: string) => {
    // Voice note logged in VoiceAnnotation component
    setCurrentVoiceAnnotation(finalText);
  };

  const toggleItemSelection = (index: number) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Item-specific voice recording handlers
  const handleItemVoiceStart = (index: number) => {
    setRecordingItemIndex(index);
    setCurrentItemTranscription(''); // Clear previous transcription
    setIsRecordingVoice(true);
  };

  const handleItemVoiceStop = () => {
    setIsRecordingVoice(false);
    setRecordingItemIndex(null);
    setCurrentItemTranscription(''); // Clear transcription when stopped
  };

  const handleItemTranscriptionUpdate = (text: string) => {
    // Update real-time transcription display
    setCurrentItemTranscription(text);
  };

  const handleItemTranscriptionComplete = (index: number, text: string) => {
    // Store voice note for this item
    setItemVoiceNotes(prev => {
      const newMap = new Map(prev);
      newMap.set(index, text);
      return newMap;
    });
    
    // Update cropped images with voice annotation
    setCroppedImages(prev => prev.map((img, i) => 
      i === index ? { ...img, voiceAnnotation: text } : img
    ));
    
    setRecordingItemIndex(null);
    setIsRecordingVoice(false);
    setCurrentItemTranscription(''); // Clear transcription when complete
  };

  const handleConfirmDescription = () => {
    if (pendingImageData) {
      // Filter cropped images to only include selected items
      const selectedCroppedImages = croppedImages.length > 0
        ? croppedImages.filter((_, index) => selectedItems.has(index))
        : undefined;
      
      // Call the onCapture callback with the image data, description, and selected cropped images
      onCapture(
        pendingImageData, 
        editableDescription || undefined,
        selectedCroppedImages
      );
      
      // Reset state for next photo
      setPendingImageData(null);
      setEditableDescription('');
      setCroppedImages([]); // Clear cropped images
      setSelectedItems(new Set()); // Clear selections
      setShowDescriptionPopup(false);
      
      // Clear and restart voice recording with proper delay
      setIsRecordingVoice(false);
      setTimeout(() => {
        handleClearRecording();
        setTimeout(() => {
          setIsRecordingVoice(true);
        }, 300);
      }, 200);
    }
  };

  const handleCancelDescription = () => {
    // Reset state
    setPendingImageData(null);
    setEditableDescription('');
    setCroppedImages([]); // Clear cropped images
    setSelectedItems(new Set()); // Clear selections
    setShowDescriptionPopup(false);
    
    // Clear and restart voice recording with proper delay
    setIsRecordingVoice(false);
    setTimeout(() => {
      handleClearRecording();
      setTimeout(() => {
        setIsRecordingVoice(true);
      }, 300);
    }, 200);
  };

  // Initialize detection service based on settings
  useEffect(() => {
    if (!isOpen) return;

    const initDetection = async () => {
      const settings = getAISettings();
      
      // Don't initialize detection if multiple items per photo is disabled
      if (!settings.multipleItemsPerPhoto) {
        console.log('⏸️ Detection skipped: Multiple Items Per Photo is disabled');
        return;
      }
      
      if (settings.detectionModel === 'none') {
        // Detection disabled in settings
        return;
      }

      setIsInitializingDetection(true);
      setDetectionError(null);

      try {
        const service = new BrowserDetectionService();
        await service.initialize(settings.detectionModel);
        setDetectionService(service);
        console.log(`✅ Detection ready (${settings.detectionModel} model)`);
      } catch (error: any) {
        console.error('❌ Detection failed:', error.message);
        setDetectionError(error.message || 'Failed to initialize detection');
      } finally {
        setIsInitializingDetection(false);
      }
    };

    initDetection();
  }, [isOpen]);

  // Draw bounding boxes on overlay canvas
  const drawBoundingBoxes = useCallback((detections: Detection[]) => {
    const canvas = overlayCanvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video) return;

    // Sync canvas size with video dimensions
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear previous drawings
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (detections.length === 0) {
      return; // Nothing to draw
    }

    // Draw each detection
    detections.forEach((detection, index) => {
      const { x, y, width, height } = detection.boundingBox;
      
      // Draw bounding box
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, width, height);

      // Draw label background
      const label = `${detection.class} (${Math.round(detection.confidence * 100)}%)`;
      ctx.font = '16px Arial';
      const textWidth = ctx.measureText(label).width;
      const textHeight = 20;

      ctx.fillStyle = 'rgba(0, 255, 0, 0.8)';
      ctx.fillRect(x, y - textHeight - 4, textWidth + 10, textHeight + 4);

      // Draw label text
      ctx.fillStyle = '#000000';
      ctx.fillText(label, x + 5, y - 8);
    });
  }, []);

  // Real-time detection loop
  const runDetectionLoop = useCallback(async () => {
    if (!detectionService || !videoRef.current || !detectionService.isReady()) {
      return;
    }

    const settings = getAISettings();
    
    // Don't run detection if multiple items per photo is disabled
    if (!settings.multipleItemsPerPhoto) {
      return;
    }
    
    // Only run detection if showRealtimeBoxes is enabled
    if (!settings.showRealtimeBoxes) {
      return;
    }

    // Check if video has valid dimensions before attempting detection
    if (videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
      // Video not ready yet, try again in next loop
      detectionLoopRef.current = window.setTimeout(runDetectionLoop, 333);
      return;
    }

    try {
      // Pass detection settings to the service
      const result = await detectionService.detect(videoRef.current, {
        maxNumBoxes: settings.maxDetections,
        minScore: settings.detectionConfidence / 100 // Convert 0-100 to 0-1
      });
      
      // Filter by confidence threshold (redundant but keeps consistency)
      const filtered = result.detections.filter(
        d => d.confidence * 100 >= settings.detectionConfidence
      );
      
      // Only log when detections change significantly
      if (filtered.length !== detections.length) {
        if (filtered.length > 0) {
          console.log(`📦 Detected ${filtered.length} item${filtered.length > 1 ? 's' : ''}: ${filtered.map(d => d.class).join(', ')}`);
        }
      }
      
      setDetections(filtered);
      
      // Draw bounding boxes
      drawBoundingBoxes(filtered);
      
    } catch (error) {
      console.error('Detection error:', error);
    }

    // Continue loop (throttled to ~3 FPS for performance)
    detectionLoopRef.current = window.setTimeout(runDetectionLoop, 333);
  }, [detectionService, drawBoundingBoxes]);

  // Start detection loop when service is ready (but pause when description popup is shown)
  useEffect(() => {
    const settings = getAISettings();
    
    // Stop detection loop if description popup is shown
    if (showDescriptionPopup) {
      if (detectionLoopRef.current) {
        clearTimeout(detectionLoopRef.current);
        detectionLoopRef.current = null;
      }
      // Clear bounding boxes overlay when popup is shown
      if (overlayCanvasRef.current) {
        const ctx = overlayCanvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
        }
      }
      return;
    }
    
    if (detectionService && 
        detectionService.isReady() && 
        stream && 
        settings.multipleItemsPerPhoto &&
        settings.detectionModel !== 'none' &&
        settings.showRealtimeBoxes) {
      runDetectionLoop();
      
      return () => {
        if (detectionLoopRef.current) {
          clearTimeout(detectionLoopRef.current);
          detectionLoopRef.current = null;
        }
      };
    }
  }, [detectionService, stream, runDetectionLoop, showDescriptionPopup]);

  // Cleanup detection service on unmount
  useEffect(() => {
    return () => {
      if (detectionLoopRef.current) {
        clearTimeout(detectionLoopRef.current);
      }
    };
  }, []);

  // Start camera when modal opens
  React.useEffect(() => {
    if (isOpen && !stream) {
      setIsProcessing(false); // Reset processing state
      setIsRecordingVoice(true); // Default voice recording to ON
      setCurrentVoiceAnnotation(''); // Reset voice annotation
      startCamera();
    } else if (!isOpen && stream) {
      stopCamera();
    }
  }, [isOpen, stream, startCamera, stopCamera]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Camera View */}
      <div className="relative w-full h-full flex items-center justify-center">
        {error ? (
          <div className="text-center p-6">
            <div className="text-white mb-4">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-lg font-medium mb-2">Camera Access Required</p>
              <p className="text-sm opacity-75">{error}</p>
            </div>
            <button
              onClick={startCamera}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Bounding boxes overlay canvas */}
            <canvas
              ref={overlayCanvasRef}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ zIndex: 10 }}
            />
            
            {/* Camera overlay grid */}
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
              <div className="w-full h-full grid grid-cols-3 grid-rows-3 opacity-30">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="border border-white/20"></div>
                ))}
              </div>
            </div>

            {/* Detection status indicator */}
            {isInitializingDetection && (
              <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center space-x-2" style={{ zIndex: 20 }}>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span className="text-white text-xs">Loading detection...</span>
              </div>
            )}

            {detectionError && (
              <div className="absolute top-4 left-4 bg-red-500/80 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center space-x-2" style={{ zIndex: 20 }}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L4.082 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-white text-xs">{detectionError}</span>
              </div>
            )}

            {detectionService && detectionService.isReady() && detections.length > 0 && (
              <div className="absolute top-4 right-4 bg-green-500/80 backdrop-blur-sm rounded-lg px-3 py-2" style={{ zIndex: 20 }}>
                <span className="text-white text-xs font-medium">{detections.length} item{detections.length !== 1 ? 's' : ''} detected</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Controls */}
      {!error && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm pb-safe">
          {/* Processing indicator */}
          {isProcessing && (
            <div className="text-center py-2">
              <div className="flex items-center justify-center space-x-2 text-white/80">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span className="text-sm">Processing photo...</span>
              </div>
            </div>
          )}
          
          {/* Voice Annotation Overlay - Only show when NOT in multiple items mode */}
          {!getAISettings().multipleItemsPerPhoto && (
            <div className="relative">
              <VoiceAnnotation
                isRecording={isRecordingVoice}
                onStartRecording={handleStartRecording}
                onStopRecording={handleStopRecording}
                onPauseRecording={handlePauseRecording}
                onClearRecording={handleClearRecording}
                onTranscriptionUpdate={handleTranscriptionUpdate}
                onTranscriptionComplete={handleTranscriptionComplete}
              />
            </div>
          )}
          
          {/* Two Button Layout */}
          <div className="flex items-center justify-center space-x-12 p-6">
            {/* Capture Photo Button */}
            <button
              onClick={capturePhoto}
              disabled={isCapturing || !stream}
              className={`
                w-20 h-20 rounded-full border-4 border-white bg-white/20 
                flex items-center justify-center transition-all duration-200
                ${isCapturing 
                  ? 'scale-95 opacity-50' 
                  : 'hover:bg-white/30 active:scale-95'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
              aria-label="Capture photo"
            >
              {isCapturing ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="w-20 h-20 rounded-full border-4 border-white bg-white/20 hover:bg-white/30 active:scale-95 flex items-center justify-center transition-all duration-200"
              aria-label="Close camera"
            >
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Description Popup Modal */}
      {showDescriptionPopup && (
        <div className="absolute inset-0 z-60 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-primary-500 text-white p-6">
              <div className="flex items-center space-x-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <h3 className="text-xl font-semibold">Item Description</h3>
                  <p className="text-sm text-white/80 mt-1">Review and edit the description</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {croppedImages.length > 0 ? (
                // Multiple detected items - show all cropped images
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Detected Items ({croppedImages.length})
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                      Select which items to create ({selectedItems.size} of {croppedImages.length} selected)
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {croppedImages.map((cropped, index) => (
                        <div key={index} className="space-y-2">
                          <div 
                            className={`relative aspect-square bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden border-2 transition-all ${
                              recordingItemIndex === index
                                ? 'border-red-500 ring-2 ring-red-500 ring-offset-2'
                                : selectedItems.has(index)
                                ? 'border-primary-500 ring-2 ring-primary-500 ring-offset-2'
                                : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                            }`}
                          >
                            {/* Clickable area for selection (top half) */}
                            <div 
                              className="absolute inset-0 cursor-pointer z-10"
                              style={{ height: '60%' }}
                              onClick={() => toggleItemSelection(index)}
                            />
                            
                            {/* Long-press area for voice recording (bottom half) */}
                            <div 
                              className="absolute bottom-0 left-0 right-0 cursor-pointer z-10 flex items-center justify-center"
                              style={{ height: '40%' }}
                              onMouseDown={() => handleItemVoiceStart(index)}
                              onMouseUp={handleItemVoiceStop}
                              onMouseLeave={handleItemVoiceStop}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                handleItemVoiceStart(index);
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                handleItemVoiceStop();
                              }}
                            >
                              {recordingItemIndex === index && (
                                <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                                  <div className="w-8 h-8 bg-red-500 rounded-full animate-pulse flex items-center justify-center">
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            <img
                              src={cropped.imageData}
                              alt={`Detected ${cropped.detection.class}`}
                              className={`w-full h-full object-cover transition-opacity ${
                                selectedItems.has(index) ? 'opacity-100' : 'opacity-50'
                              }`}
                            />
                            
                            {/* Checkbox overlay */}
                            <div className="absolute top-2 right-2 bg-white dark:bg-slate-800 rounded-full p-1 shadow-lg z-20">
                              {selectedItems.has(index) ? (
                                <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                                  <circle cx="10" cy="10" r="8" strokeWidth="2" />
                                </svg>
                              )}
                            </div>
                            
                            {/* Microphone icon indicator (bottom left) */}
                            <div className="absolute bottom-2 left-2 bg-slate-800/80 rounded-full p-1.5 shadow-lg z-20">
                              <svg className={`w-3 h-3 ${cropped.voiceAnnotation ? 'text-green-400' : 'text-slate-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                          <div className="text-center relative">
                            <p className={`text-xs font-medium ${
                              selectedItems.has(index) 
                                ? 'text-slate-700 dark:text-slate-300' 
                                : 'text-slate-400 dark:text-slate-500'
                            }`}>
                              {cropped.detection.class}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {Math.round(cropped.detection.confidence * 100)}% confident
                            </p>
                            {cropped.voiceAnnotation && (
                              <div className="relative">
                                <p 
                                  className="text-xs text-green-600 dark:text-green-400 mt-1 truncate cursor-pointer hover:text-green-700 dark:hover:text-green-300"
                                  onMouseEnter={() => setHoveredItemIndex(index)}
                                  onMouseLeave={() => setHoveredItemIndex(null)}
                                  onClick={() => setHoveredItemIndex(hoveredItemIndex === index ? null : index)}
                                >
                                  🎤 "{cropped.voiceAnnotation.substring(0, 20)}{cropped.voiceAnnotation.length > 20 ? '...' : ''}"
                                </p>
                                
                                {/* Hover tooltip showing full text */}
                                {hoveredItemIndex === index && (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50 w-64 p-3 bg-slate-900 dark:bg-slate-800 text-white rounded-lg shadow-xl border border-slate-700">
                                    <div className="text-xs font-semibold mb-1 text-green-400">Voice Note:</div>
                                    <p className="text-sm leading-relaxed break-words">
                                      {cropped.voiceAnnotation}
                                    </p>
                                    {/* Arrow pointer */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                                      <div className="border-8 border-transparent border-t-slate-900 dark:border-t-slate-800"></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Item-specific voice recording */}
                  {recordingItemIndex !== null && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <p className="text-sm font-medium text-red-700 dark:text-red-300">
                          Recording description for {croppedImages[recordingItemIndex].detection.class}...
                        </p>
                      </div>
                      
                      {/* Real-time transcription display */}
                      {currentItemTranscription && (
                        <div className="mb-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-red-300 dark:border-red-700">
                          <p className="text-base text-slate-800 dark:text-slate-200 leading-relaxed">
                            {currentItemTranscription}
                          </p>
                        </div>
                      )}
                      
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {currentItemTranscription ? 'Keep holding to continue...' : 'Speak now...'}
                      </p>
                      
                      <VoiceAnnotation
                        isRecording={recordingItemIndex !== null}
                        onStartRecording={() => {}}
                        onStopRecording={handleItemVoiceStop}
                        onPauseRecording={handleItemVoiceStop}
                        onClearRecording={() => {}}
                        onTranscriptionUpdate={handleItemTranscriptionUpdate}
                        onTranscriptionComplete={(text) => handleItemTranscriptionComplete(recordingItemIndex, text)}
                      />
                    </div>
                  )}

                  {/* Help text for multiple items */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-xs text-blue-700 dark:text-blue-300">
                        <p className="font-medium mb-1">How to add voice descriptions:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li><strong>Tap top</strong> of item to select/deselect</li>
                          <li><strong>Hold bottom</strong> of item to record voice description</li>
                          <li>Green microphone = has voice note</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      General Description (Optional)
                    </label>
                    <textarea
                      value={editableDescription}
                      onChange={(e) => setEditableDescription(e.target.value)}
                      className="flex-1 w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg 
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                               focus:ring-2 focus:ring-primary-500 focus:border-transparent
                               resize-none min-h-[120px]"
                      placeholder="Add a general description for all items..."
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                      This description will be used for all {selectedItems.size} selected item{selectedItems.size !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ) : (
                // Single item - show full photo
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Image Preview */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Photo Preview
                    </label>
                    <div className="aspect-square bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden border-2 border-slate-200 dark:border-slate-600">
                      {pendingImageData && (
                        <img
                          src={pendingImageData}
                          alt="Captured item"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="flex flex-col">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Description from Voice Note
                    </label>
                    <textarea
                      value={editableDescription}
                      onChange={(e) => setEditableDescription(e.target.value)}
                      className="flex-1 w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg 
                               bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                               focus:ring-2 focus:ring-primary-500 focus:border-transparent
                               resize-none min-h-[200px]"
                      placeholder="Add or edit the item description..."
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                      You can add more details or correct any errors from the voice transcription
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={handleCancelDescription}
                className="px-6 py-3 rounded-lg font-medium text-slate-700 dark:text-slate-300 
                         hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDescription}
                disabled={croppedImages.length > 0 && selectedItems.size === 0}
                className="px-6 py-3 rounded-lg font-medium bg-primary-500 text-white 
                         hover:bg-primary-600 transition-colors shadow-lg hover:shadow-xl
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-500"
              >
                {croppedImages.length > 0 
                  ? `Create ${selectedItems.size} Item${selectedItems.size !== 1 ? 's' : ''}`
                  : 'Save & Continue'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
